/**
 * @file NegativeImpactCategoriesEditor Component
 * @description Entity-specific editor for Negative Impact Categories lookup table.
 * Categories classify types of negative impacts/risks (e.g., Financial Loss,
 * Compliance/Regulatory, Client Risk, etc.)
 */

import {
  useNegativeImpactCategories,
  useCreateNegativeImpactCategory,
  useUpdateNegativeImpactCategory,
  useDeleteNegativeImpactCategory,
} from '@medsuite/api-client';

import EntityEditor from '../components/EntityEditor';

interface NegativeImpactCategoriesEditorProps {
  onDataLoaded?: (count: number) => void;
}

export default function NegativeImpactCategoriesEditor({
  onDataLoaded,
}: NegativeImpactCategoriesEditorProps) {
  const { data: categories = [], isLoading, error } = useNegativeImpactCategories();
  const addMutation = useCreateNegativeImpactCategory();
  const updateMutation = useUpdateNegativeImpactCategory();
  const deleteMutation = useDeleteNegativeImpactCategory();

  const columns = [
    { key: 'id', label: 'ID', readonly: true },
    { key: 'name', label: 'Category Name', type: 'text' as const },
    { key: 'description', label: 'Description', type: 'text' as const },
    { key: 'sort_order', label: 'Sort Order', type: 'number' as const },
    {
      key: 'is_active',
      label: 'Active',
      type: 'select' as const,
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
    },
  ];

  // Transform data to handle boolean display
  const transformedData = categories.map((cat) => ({
    ...cat,
    is_active: cat.is_active ? 'true' : 'false',
  }));

  return (
    <EntityEditor
      title="Negative Impact Categories"
      columns={columns}
      data={transformedData as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
      primaryKey="id"
      onDataLoaded={onDataLoaded}
      onAdd={async (item) => {
        const { name, description, sort_order, is_active } = item as {
          name: string;
          description?: string;
          sort_order?: number;
          is_active?: string;
        };
        await addMutation.mutateAsync({
          name,
          description: description || null,
          sort_order: sort_order ?? 0,
          is_active: is_active === 'true',
        });
      }}
      onUpdate={async (id, item) => {
        const { name, description, sort_order, is_active } = item as {
          name?: string;
          description?: string;
          sort_order?: number;
          is_active?: string;
        };
        await updateMutation.mutateAsync({
          id: Number(id),
          data: {
            name,
            description: description ?? undefined,
            sort_order,
            is_active: is_active !== undefined ? is_active === 'true' : undefined,
          },
        });
      }}
      onDelete={(id) => deleteMutation.mutateAsync(Number(id))}
      isAdding={addMutation.isPending}
      isUpdating={updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
    />
  );
}
